import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { formatDateTime } from "../../utils/date";

const formatCurrency = (value) =>
  value === undefined || value === null || value === "" ? "-" : `₹ ${value}`;
const formatOrderId = (order) => {
  const source = order?.orderDisplayId || order?.orderId || order?.orderNumber;
  if (source && String(source).length <= 4)
    return String(source).padStart(4, "0");
  return order?.orderNumber || order?._id || "-";
};
const getLabStatus = (order) =>
  order?.labStatus || order?.deliveryStatus || "Pending";
const getAddressValue = (address, keys) => {
  for (const key of keys) {
    if (
      address?.[key] !== undefined &&
      address?.[key] !== null &&
      address?.[key] !== ""
    ) {
      return address[key];
    }
  }
  return "-";
};
const getSlotPeriod = (slot) =>
  slot?.slotPeriod ||
  slot?.period ||
  [slot?.startTime, slot?.endTime].filter(Boolean).join(" - ") ||
  "-";
const getItemParameters = (item) => {
  const values = item.parameters || item.tests || item.testDetails || [];
  if (Array.isArray(values) && values.length) {
    return values
      .map((entry) => entry?.name || entry?.title || entry)
      .join(", ");
  }
  return item.totalParameters ?? "-";
};

const ViewOrder = () => {
  const { id, userId, orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const isUserScopedOrder = Boolean(userId && orderId);
        const endpoint = isUserScopedOrder
          ? `/admin/user/${userId}/orders/${orderId}`
          : `/admin/orders/${id}`;
        const res = await AuthInterceptor(endpoint, {
          method: "GET",
        });
        setOrder(res?.data?.s === 200 && res.data?.r ? res.data.r : null);
      } catch (error) {
        setSnack({
          notification: {
            message: "Failed to fetch order details",
            type: "error",
          },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, userId, orderId]);

  if (loading) return <Spinnerr show={true} />;

  if (!order) {
    return (
      <div className="card p-4 text-center">
        <p className="text-muted mb-3">Order not found</p>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>
    );
  }

  const members = Array.isArray(order.members) ? order.members : [];
  const allItems = members.flatMap((member) =>
    (member.items || []).map((item) => ({
      ...item,
      memberName: member.fullName,
    })),
  );

  return (
    <>
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Order Details</h4>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>

        <Section title="Order Information">
          <Info label="Order ID" value={formatOrderId(order)} />
          <Info label="Order Number" value={order.orderNumber} />
          <Info label="Order Date" value={formatDateTime(order.createdAt)} />
          <Info label="Order Status" value={order.orderStatus || "Pending"} />
          <Info label="Lab Status" value={getLabStatus(order)} />
          <Info label="Last Update Date" value={formatDateTime(order.updatedAt)} />
        </Section>

        <Section title="Amount">
          <Info
            label="Total Amount"
            value={formatCurrency(order.totalAmount)}
          />
          <Info
            label="Discount Value"
            value={formatCurrency(order.totalDiscount)}
          />
          <Info
            label="Final Amount"
            value={formatCurrency(order.finalAmount)}
          />
          <Info
            label="Discount %"
            value={
              order.totalPercent !== undefined
                ? `${Math.floor(order.totalPercent).toFixed(2)}%`
                : "-"
            }
          />
          <Info
            label="Coupon Discount"
            value={formatCurrency(order.couponDiscountAmount)}
          />
        </Section>

        <Section title="Address Details">
          <Info
            label="Type"
            value={getAddressValue(order.address, ["type", "addressType"])}
          />
          <Info
            label="House No./Flat No."
            value={getAddressValue(order.address, [
              "houseNo",
              "flatNo",
              "houseNumber",
            ])}
          />
          <Info
            label="Apartment/Building/Colony"
            value={getAddressValue(order.address, [
              "apartment",
              "building",
              "colony",
              "addressLine1",
            ])}
          />
          <Info
            label="Landmark"
            value={getAddressValue(order.address, ["landmark", "landMark"])}
          />
          <Info
            label="Pincode"
            value={getAddressValue(order.address, ["pincode", "pinCode"])}
          />
          <Info
            label="Latitude"
            value={getAddressValue(order.address, ["latitude", "lat"])}
          />
          <Info
            label="Longitude"
            value={getAddressValue(order.address, ["longitude", "lng", "long"])}
          />
        </Section>

        <Section title="Slot Details">
          <Info label="Date" value={order.slot?.date || "-"} />
          <Info label="Slot Period" value={getSlotPeriod(order.slot)} />
        </Section>

        <Section title="Members Details">
          {members.length > 0 ? (
            members.map((member, index) => (
              <div key={member._id || index} className="col-12">
                <div className="border rounded-3 p-3">
                  <h6 className="mb-3">
                    Member {index + 1}: {member.fullName || "-"}
                  </h6>
                  <div className="row g-3">
                    <Info label="Gender" value={member.gender} />
                    <Info label="Age" value={member.age} />
                    <Info label="Relation" value={member.relation} />
                    <Info label="Mobile" value={member.mobileNumber} />
                    <Info label="Email" value={member.email} />
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12 text-muted">No members found</div>
          )}
        </Section>

        <Section title="Order Item">
          <div className="col-12">
            {allItems.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Member</th>
                      <th>Type</th>
                      <th>Title</th>
                      <th>Original Price</th>
                      <th>Discounted Price</th>
                      <th>Discount %</th>
                      <th>Parameters/Tests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allItems.map((item, index) => (
                      <tr key={item._id || index}>
                        <td>{item.memberName || "-"}</td>
                        <td>{item.type || "-"}</td>
                        <td>{item.title || "-"}</td>
                        <td>{formatCurrency(item.originalPrice)}</td>
                        <td>{formatCurrency(item.discountedPrice)}</td>
                        <td>
                          {item.discountPercent !== undefined
                            ? `${item.discountPercent}%`
                            : "-"}
                        </td>
                        <td>{getItemParameters(item)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-muted">No order items found</div>
            )}
          </div>
        </Section>
      </div>
    </>
  );
};

const Section = ({ title, children }) => (
  <>
    <h5 className="mt-4 mb-3">{title}</h5>
    <div className="row g-3">{children}</div>
    <hr />
  </>
);

const Info = ({ label, value }) => (
  <div className="col-md-4">
    <div className="text-muted small">{label}</div>
    <div className="fw-semibold">{value || "-"}</div>
  </div>
);

export default ViewOrder;
